import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import { FuturisticFemaleIcon, FuturisticMaleIcon } from './ui/GenderFigureIcons';
import manFrontFigure from '../assets/pain-map/man-front.jpg';
import manBackFigure from '../assets/pain-map/man-back.jpg';
import womanFrontFigure from '../assets/pain-map/woman-front.jpg';
import womanBackFigure from '../assets/pain-map/woman-back.jpg';
import manPreviewVideo from '../assets/pain-map/man.mp4';
import womanPreviewVideo from '../assets/pain-map/woman.mp4';
import {
  calculateCentroid,
  detectZoneFromSelection,
  getDistance,
  getSvgPointFromClient,
  toFigureCoordinates,
  toViewBoxCircle,
  toViewBoxPoint,
} from '../utils/coordinateMapper';
import { buildUnifiedEventHandlers, getClientPointFromEvent } from '../utils/eventHandler';

/** Preview clips finish a full turn ~4.875s; trailing frames hold on the front pose. */
const PREVIEW_LOOP_END_SEC = 4.875;

function circleFromPoints(points, fixedCenter = null, useLastPointRadius = false) {
  if (!points?.length) return null;
  const center = fixedCenter || calculateCentroid(points);
  if (!center) return null;
  const radiusSource = useLastPointRadius ? points[points.length - 1] : null;
  const radius = radiusSource
    ? Math.max(1.4, getDistance(radiusSource, center))
    : Math.max(1.4, ...points.map((point) => getDistance(point, center)));
  return { cx: center.x, cy: center.y, r: radius };
}

/** Same luminance-to-alpha cutout used for static JPG figures. */
function FigureCutoutFilter({ id }) {
  return (
    <filter id={id} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
      <feColorMatrix
        type="matrix"
        values="
          1 0 0 0 0
          0 1 0 0 0
          0 0 1 0 0
          0.3333 0.3333 0.3333 0 -0.015
        "
      />
      <feComponentTransfer>
        <feFuncR type="linear" slope="0.32" intercept="0.035" />
        <feFuncG type="linear" slope="0.48" intercept="0.055" />
        <feFuncB type="linear" slope="1.02" intercept="0.095" />
        <feFuncA type="gamma" amplitude="1.08" exponent="1.02" offset="0" />
      </feComponentTransfer>
      <feDropShadow dx="0" dy="0" stdDeviation="0.55" floodColor="#7BB4FF" floodOpacity="0.24" />
      <feDropShadow dx="0" dy="0" stdDeviation="1.15" floodColor="#3A7BE3" floodOpacity="0.12" />
    </filter>
  );
}

export default function PainBodyMap({
  gender,
  view,
  captureMode = false,
  pendingSelection,
  confirmedSelections = [],
  interactionLocked,
  onGenderChange,
  onViewChange,
  onPendingSelection,
  onBlockedInteraction,
  onConfirmLocation,
  onClearLocation,
  onDragStart,
  onDragEnd,
}) {
  const svgRef = useRef(null);
  const videoRef = useRef(null);
  const startRef = useRef(0);
  const swipeStartRef = useRef(null);
  const [, setResizeTick] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);
  const [pulseSelection, setPulseSelection] = useState(false);

  useEffect(() => {
    if (!svgRef.current) return undefined;
    const observer = new ResizeObserver(() => setResizeTick((v) => v + 1));
    observer.observe(svgRef.current);
    return () => observer.disconnect();
  }, [captureMode]);

  const previewVideo = gender === 'woman' ? womanPreviewVideo : manPreviewVideo;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (captureMode) {
      video.pause();
      return undefined;
    }

    let cancelled = false;
    let frameCallbackId = null;

    const rewindToLoopStart = () => {
      video.currentTime = 0.001;
    };

    const onFrame = () => {
      if (cancelled) return;
      if (video.currentTime >= PREVIEW_LOOP_END_SEC) {
        rewindToLoopStart();
      }
      if (video.requestVideoFrameCallback) {
        frameCallbackId = video.requestVideoFrameCallback(onFrame);
      }
    };

    const onTimeUpdate = () => {
      if (video.currentTime >= PREVIEW_LOOP_END_SEC) {
        rewindToLoopStart();
      }
    };

    video.load();
    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }

    if (video.requestVideoFrameCallback) {
      frameCallbackId = video.requestVideoFrameCallback(onFrame);
    } else {
      video.addEventListener('timeupdate', onTimeUpdate);
    }

    return () => {
      cancelled = true;
      if (frameCallbackId != null && video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(frameCallbackId);
      }
      video.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [captureMode, previewVideo, gender]);

  const baseImage =
    gender === 'woman'
      ? view === 'front'
        ? womanFrontFigure
        : womanBackFigure
      : view === 'front'
        ? manFrontFigure
        : manBackFigure;

  const getNormalizedFromEvent = (event) => {
    const point = getClientPointFromEvent(event);
    if (!point || !svgRef.current) return null;
    const svgPoint = getSvgPointFromClient(svgRef.current, point.x, point.y);
    if (!svgPoint) return null;
    return toFigureCoordinates(svgPoint);
  };

  const startInteraction = (event) => {
    if (interactionLocked) {
      if (pendingSelection) {
        onBlockedInteraction?.();
      }
      return;
    }
    const point = getNormalizedFromEvent(event);
    if (!point) return;
    setIsDrawing(true);
    setDraftPoints([point]);
    startRef.current = Date.now();
    onDragStart?.();
  };

  const moveInteraction = (event) => {
    if (!isDrawing) return;
    const point = getNormalizedFromEvent(event);
    if (!point) return;
    setDraftPoints((prev) => {
      const last = prev[prev.length - 1];
      if (!last || getDistance(last, point) > 1) {
        return [...prev, point];
      }
      return prev;
    });
  };

  const finishInteraction = (event) => {
    if (!isDrawing) return;
    const endPoint = getNormalizedFromEvent(event) || draftPoints[draftPoints.length - 1];
    const duration = Date.now() - startRef.current;
    const combined = endPoint ? [...draftPoints, endPoint] : draftPoints;
    const tapLike = duration < 100 || combined.length < 3;
    const dragCenter = combined[0];
    const selectionCenter = tapLike ? endPoint : dragCenter;
    if (!selectionCenter) {
      setIsDrawing(false);
      setDraftPoints([]);
      onDragEnd?.();
      return;
    }

    const points = tapLike ? [selectionCenter] : combined;
    const fittedCircle = tapLike ? null : circleFromPoints(points, selectionCenter, true);
    const zone = detectZoneFromSelection({
      centroid: selectionCenter,
      points,
      view,
      circle: fittedCircle,
    });

    onPendingSelection?.({
      type: tapLike ? 'tap' : 'drag',
      coordinates: selectionCenter,
      points,
      circle: fittedCircle,
      zone,
      view,
      gender,
    });

    setPulseSelection(true);
    window.setTimeout(() => setPulseSelection(false), 310);
    setIsDrawing(false);
    setDraftPoints([]);
    onDragEnd?.();
  };

  const cancelInteraction = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setDraftPoints([]);
    onDragEnd?.();
  };

  const handlers = buildUnifiedEventHandlers({
    onStart: startInteraction,
    onMove: moveInteraction,
    onEnd: finishInteraction,
    onCancel: cancelInteraction,
  });

  const draftCircle = draftPoints.length > 1 ? toViewBoxCircle(circleFromPoints(draftPoints, draftPoints[0], true)) : null;
  const pendingCircle =
    pendingSelection?.type === 'drag'
      ? toViewBoxCircle(
          pendingSelection.circle
            || circleFromPoints(pendingSelection.points || [], pendingSelection.coordinates || pendingSelection.points?.[0])
        )
      : null;
  const showTap = pendingSelection?.type === 'tap' && pendingSelection.coordinates;
  const pendingTapPoint = showTap ? toViewBoxPoint(pendingSelection.coordinates) : null;
  const visibleConfirmedSelections = confirmedSelections.filter((selection) => selection?.view === view);

  const hasConfirmedSelections = visibleConfirmedSelections.length > 0;
  const showClearButton = captureMode && (Boolean(pendingSelection) || hasConfirmedSelections);
  const showConfirmButton = captureMode && Boolean(pendingSelection);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        flex: 1,
        gap: 0.5,
        px: { xs: 0.5, sm: 0.75 },
        pt: { xs: 0.35, sm: 0.5 },
        pb: { xs: 0.5, sm: 0.75 },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'nowrap', flexShrink: 0 }}>
        <Button
          variant="outlined"
          startIcon={<Autorenew />}
          onClick={() => onViewChange?.(view === 'front' ? 'back' : 'front')}
          disabled={!captureMode}
          aria-hidden={!captureMode}
          tabIndex={captureMode ? 0 : -1}
          sx={{
            minWidth: 44,
            minHeight: 44,
            textTransform: 'none',
            borderColor: 'rgba(50,175,252,0.45)',
            color: '#dce7f4',
            borderRadius: '8px',
            visibility: captureMode ? 'visible' : 'hidden',
          }}
        >
          {view === 'front' ? 'Front' : 'Back'}
        </Button>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 1, minHeight: 44 }}>
          <Button
            onClick={onConfirmLocation}
            variant="contained"
            disabled={!showConfirmButton}
            aria-hidden={!showConfirmButton}
            tabIndex={showConfirmButton ? 0 : -1}
            sx={{
              minWidth: 44,
              minHeight: 44,
              textTransform: 'none',
              borderRadius: '8px',
              position: 'relative',
              overflow: 'visible',
              px: 2,
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: '#f5fbff',
              visibility: showConfirmButton ? 'visible' : 'hidden',
              background: 'linear-gradient(135deg, #2f83ea 0%, #236dc6 100%)',
                border: '1px solid rgba(152, 220, 255, 0.82)',
                boxShadow: '0 0 0 1px rgba(120, 198, 255, 0.5), 0 0 16px rgba(84, 182, 255, 0.56), 0 0 30px rgba(70, 158, 255, 0.38)',
                animation: 'confirmAttentionPulse 0.95s ease-in-out infinite',
                '@keyframes confirmAttentionPulse': {
                  '0%': {
                    boxShadow: '0 0 0 1px rgba(120, 198, 255, 0.24), 0 0 8px rgba(84, 182, 255, 0.24), 0 0 16px rgba(70, 158, 255, 0.16)',
                    transform: 'scale(1)',
                  },
                  '50%': {
                    boxShadow: '0 0 0 1px rgba(120, 198, 255, 0.85), 0 0 22px rgba(84, 182, 255, 0.9), 0 0 42px rgba(70, 158, 255, 0.72)',
                    transform: 'scale(1.06)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 1px rgba(120, 198, 255, 0.24), 0 0 8px rgba(84, 182, 255, 0.24), 0 0 16px rgba(70, 158, 255, 0.16)',
                    transform: 'scale(1)',
                  },
                },
                '@keyframes confirmAttentionBeacon': {
                  '0%': {
                    transform: 'scale(0.88)',
                    opacity: 0.65,
                  },
                  '100%': {
                    transform: 'scale(1.28)',
                    opacity: 0,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-10px',
                  borderRadius: '14px',
                  border: '2px solid rgba(130, 214, 255, 0.9)',
                  animation: 'confirmAttentionBeacon 1.1s ease-out infinite',
                  pointerEvents: 'none',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #3a95ff 0%, #2a7de1 100%)',
                  animationDuration: '0.75s',
                },
              }}
            >
              Confirm
            </Button>
          {showClearButton && (
            <Button
              onClick={onClearLocation}
              variant="contained"
              sx={{
                minWidth: 44,
                minHeight: 44,
                textTransform: 'none',
                borderRadius: '8px',
                backgroundColor: '#6C7A8F',
                '&:hover': { backgroundColor: '#5f6b7c' },
              }}
            >
              Clear
            </Button>
          )}
        </Box>

        <ToggleButtonGroup
          value={gender}
          exclusive
          onChange={(_event, value) => value && onGenderChange?.(value)}
          size="small"
          aria-label="Gender toggle"
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(50,175,252,0.45)',
            '& .MuiToggleButtonGroup-grouped': {
              borderRadius: 0,
              border: 0,
              mx: 0,
            },
            '& .MuiToggleButton-root': { minWidth: 44, minHeight: 44, color: 'rgba(227,235,241,0.55)' },
            '& .Mui-selected': { color: '#32affc' },
          }}
        >
          <ToggleButton value="man" aria-label="Male figure">
            <FuturisticMaleIcon size={22} />
          </ToggleButton>
          <ToggleButton value="woman" aria-label="Female figure">
            <FuturisticFemaleIcon size={22} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          borderRadius: '8px',
          overflow: 'hidden',
          border: 'none',
          background: 'transparent',
          touchAction: captureMode ? 'none' : 'auto',
          '@keyframes pendingPulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.05)' },
            '100%': { transform: 'scale(1)' },
          },
        }}
        onTouchStart={(event) => {
          if (!captureMode) return;
          swipeStartRef.current = getClientPointFromEvent(event)?.x || null;
        }}
        onTouchEnd={(event) => {
          if (!captureMode) return;
          const endX = getClientPointFromEvent(event)?.x;
          if (swipeStartRef.current == null || endX == null || isDrawing) return;
          const delta = endX - swipeStartRef.current;
          if (Math.abs(delta) > 48) {
            onViewChange?.(delta < 0 ? 'back' : 'front');
          }
        }}
      >
        {!captureMode ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              background:
                'radial-gradient(ellipse 85% 75% at 50% 42%, rgba(18, 52, 96, 0.98) 0%, rgba(6, 18, 40, 1) 72%)',
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              src={previewVideo}
              autoPlay
              muted
              playsInline
              aria-label={`${gender} body preview`}
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                objectPosition: 'center center',
                display: 'block',
                background: 'transparent',
                mixBlendMode: 'screen',
                transform: 'scale(0.99)',
                transformOrigin: 'center center',
              }}
            />
          </Box>
        ) : (
        <svg ref={svgRef} viewBox="0 0 100 100" width="100%" height="100%" aria-label={`${gender} ${view} pain map`}>
          <defs>
            <FigureCutoutFilter id="figureCutout" />
          </defs>
          <image
            href={baseImage}
            x={0}
            y={-4}
            width={100}
            height={110}
            preserveAspectRatio="xMidYMid meet"
            filter="url(#figureCutout)"
          />

          {visibleConfirmedSelections.map((selection, index) => {
            const confirmedCircle =
              selection?.type === 'drag'
                ? toViewBoxCircle(
                    selection.circle
                      || circleFromPoints(selection.points || [], selection.coordinates || selection.points?.[0])
                  )
                : null;
            const confirmedTap =
              selection?.type === 'tap' && selection.coordinates
                ? toViewBoxPoint(selection.coordinates)
                : null;

            return (
              <React.Fragment key={`confirmed-${selection.view}-${selection.type}-${index}`}>
                {confirmedCircle && (
                  <circle
                    cx={confirmedCircle.cx}
                    cy={confirmedCircle.cy}
                    r={confirmedCircle.r}
                    fill="rgba(166, 205, 255, 0.2)"
                    stroke="#1F3E75"
                    strokeWidth="0.75"
                    style={{
                      mixBlendMode: 'screen',
                      filter: 'drop-shadow(0 0 4px rgba(112, 168, 245, 0.5))',
                    }}
                  />
                )}
                {confirmedTap && (
                  <circle
                    cx={confirmedTap.x}
                    cy={confirmedTap.y}
                    r="2.1"
                    fill="#8AC8FF"
                    stroke="#F4FAFF"
                    strokeWidth="0.9"
                    style={{ filter: 'drop-shadow(0 0 5px rgba(160, 211, 255, 0.9))' }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {draftCircle && (
            <circle
              cx={draftCircle.cx}
              cy={draftCircle.cy}
              r={draftCircle.r}
              fill="rgba(166, 205, 255, 0.16)"
              stroke="#1F3E75"
              strokeWidth="0.7"
              style={{ mixBlendMode: 'screen' }}
            />
          )}
          {pendingCircle && (
            <circle
              cx={pendingCircle.cx}
              cy={pendingCircle.cy}
              r={pendingCircle.r}
              fill="rgba(166, 205, 255, 0.2)"
              stroke="#1F3E75"
              strokeWidth="0.75"
              style={{
                mixBlendMode: 'screen',
                filter: 'drop-shadow(0 0 4px rgba(112, 168, 245, 0.55))',
                animation: pulseSelection ? 'pendingPulse 0.3s ease' : 'none',
              }}
            />
          )}
          {showTap && pendingTapPoint && (
            <circle
              cx={pendingTapPoint.x}
              cy={pendingTapPoint.y}
              r="2.1"
              fill="#8AC8FF"
              stroke="#F4FAFF"
              strokeWidth="0.9"
              style={{
                filter: 'drop-shadow(0 0 5px rgba(160, 211, 255, 0.88))',
                animation: pulseSelection ? 'pendingPulse 0.3s ease' : 'none',
              }}
            />
          )}
        </svg>
        )}
        {captureMode && <Box {...handlers} sx={{ position: 'absolute', inset: 0 }} />}

      </Box>

    </Box>
  );
}

